import {Card, CardBody, Divider, Input, Spacer, Button} from "@heroui/react";
import { Icon } from "@iconify/react";
import React, { useState } from "react";
import { format } from "date-fns";
import {CalendarDate, parseAbsoluteToLocal, parseDate} from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { CurrencyInput, DateInput, MonthSelect, YearInput } from "../../components/inputs";
import { apiClientExporter } from "../../services/apiClient";

function DetalleJudicialEditar({ judicialData, readOnlyClaims = false }) {
    // Estado para controlar el modo de edición
    const [isEditing, setIsEditing] = useState(false);
    
    // Estado para almacenar los valores editables
    const [formData, setFormData] = useState({
        solicitante: judicialData?.solicitante ?? "",
        solicitado: judicialData?.solicitado ?? "",
        demandado: judicialData?.demandado ?? "",
        tribunal: judicialData?.tribunal ?? "",
        rol: judicialData?.rol ?? "",
        caratula: judicialData?.caratula ?? "",
        ref_fester: judicialData?.ref_fester ?? "",
        ref_mf: judicialData?.ref_mf ?? "",
        estado: judicialData?.estado ?? "",
        encargado: judicialData?.encargado ?? "",
        cuantia_usd: judicialData?.cuantia_usd ?? "",
        cuantia_eur: judicialData?.cuantia_eur ?? "",
        comentarios: judicialData?.comentarios ?? "",
        reclamo_extrajudicial: judicialData?.reclamo_extrajudicial ?? "",
        arbitro_designado: judicialData?.arbitro_designado ?? "",
        juez_arbitro: judicialData?.juez_arbitro ?? "",
        demandante: judicialData?.demandante ?? "",
        merito: judicialData?.merito ?? "",
        etapa_procesal: judicialData?.etapa_procesal ?? "",
        plazo_arbitraje: judicialData?.plazo_arbitraje ?? "",
        monto_recuperado: judicialData?.monto_recuperado ?? "",
        procedimiento: judicialData?.procedimiento ?? "",
        id: judicialData?.judicial_id ?? "", // Usar judicial_id en lugar de id
        maestra_recuperos_id: judicialData?.maestra_recuperos_id ?? "",
    });

    // Manejador para actualizar los valores de los campos
    const handleInputChange = (field, value) => {
        console.log("field", field);
        console.log("value", value);
        setFormData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };

    const handleDateChange = (field, newValue) => {
        console.log("field", field);
        console.log("value", newValue);
        const formattedDate = newValue
            ? format(new Date(newValue.year, newValue.month - 1, newValue.day), "yyyy-MM-dd")
            : null;

        setFormData((prevData) => ({
            ...prevData,
            [field]: formattedDate,
        }));
    };

    // Manejador para guardar los cambios
    const handleSave = async () => {
        try {
            const token = localStorage.getItem("exporterAuthToken");
            
            // Limpiar datos antes de enviar - convertir strings vacíos a null para campos numéricos
            const cleanedData = {
                ...formData,
                cuantia_usd: formData.cuantia_usd === "" ? null : formData.cuantia_usd,
                cuantia_eur: formData.cuantia_eur === "" ? null : formData.cuantia_eur,
                id: formData.id === "" ? null : formData.id,
                // Asegurar que maestra_recuperos_id no sea null si existe
                maestra_recuperos_id: formData.maestra_recuperos_id || judicialData?.id || null
            };
            
            console.log("Datos a enviar:", cleanedData);
            console.log("judicialData:", judicialData);
            
            const response = await apiClientExporter.put('/exporter/maestra/judicial', cleanedData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            
            console.log("Datos guardados exitosamente:", response.data);
            alert("Datos guardados exitosamente");
            setIsEditing(false);
        } catch (error) {
            console.error("Error al guardar los datos:", error);
            alert("Error al guardar los datos. Por favor, intenta de nuevo.");
        }
    };

    // Manejador para alternar el modo de edición
    const toggleEditMode = () => {
        if (readOnlyClaims) return;
        setIsEditing(!isEditing);
    };

    const identificationFields = [
        { icon: "lucide:hash", label: "ID Judicial", fieldName: "id", value: formData.id, tipo: "readonly" },
        { icon: "lucide:hash", label: "Maestra Recuperos ID", fieldName: "maestra_recuperos_id", value: formData.maestra_recuperos_id, tipo: "readonly" },
        { icon: "lucide:user", label: "Solicitante", fieldName: "solicitante", value: formData.solicitante },
        { icon: "lucide:user-check", label: "Solicitado", fieldName: "solicitado", value: formData.solicitado },
        { icon: "lucide:user-x", label: "Demandado", fieldName: "demandado", value: formData.demandado },
        { icon: "lucide:building", label: "Tribunal", fieldName: "tribunal", value: formData.tribunal },
        { icon: "lucide:hash", label: "Rol", fieldName: "rol", value: formData.rol },
        { icon: "lucide:file-text", label: "Carátula", fieldName: "caratula", value: formData.caratula },
    ];

    const referenceFields = [
        { icon: "lucide:hash", label: "Ref. Fester", fieldName: "ref_fester", value: formData.ref_fester },
        { icon: "lucide:hash", label: "Ref. MF", fieldName: "ref_mf", value: formData.ref_mf },
        { icon: "lucide:hash", label: "Estado", fieldName: "estado", value: formData.estado },
        { icon: "lucide:user", label: "Encargado", fieldName: "encargado", value: formData.encargado },
        { icon: "lucide:dollar-sign", label: "Cuantía USD", fieldName: "cuantia_usd", value: formData.cuantia_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:euro", label: "Cuantía EUR", fieldName: "cuantia_eur", value: formData.cuantia_eur, tipo: "moneda", currency: "EUR" },
        { icon: "lucide:message-square", label: "Comentarios", fieldName: "comentarios", value: formData.comentarios },
    ];

    const legalFields = [
        { icon: "lucide:gavel", label: "Reclamo Extrajudicial", fieldName: "reclamo_extrajudicial", value: formData.reclamo_extrajudicial },
        { icon: "lucide:scale", label: "Árbitro Designado", fieldName: "arbitro_designado", value: formData.arbitro_designado },
        { icon: "lucide:scale-3d", label: "Juez Árbitro", fieldName: "juez_arbitro", value: formData.juez_arbitro },
        { icon: "lucide:user", label: "Demandante", fieldName: "demandante", value: formData.demandante },
        { icon: "lucide:hash", label: "Mérito", fieldName: "merito", value: formData.merito },
        { icon: "lucide:hash", label: "Etapa Procesal", fieldName: "etapa_procesal", value: formData.etapa_procesal },
        { icon: "lucide:clock", label: "Plazo Arbitraje", fieldName: "plazo_arbitraje", value: formData.plazo_arbitraje },
        { icon: "lucide:dollar-sign", label: "Monto Recuperado", fieldName: "monto_recuperado", value: formData.monto_recuperado },
        { icon: "lucide:hash", label: "Procedimiento", fieldName: "procedimiento", value: formData.procedimiento },
    ];

    // Función para renderizar el campo apropiado según su tipo
    const renderField = (field, index) => {
        const commonProps = {
            label: field.label,
            value: field.value,
            onChange: (value) => handleInputChange(field.fieldName, value),
            isReadOnly: field.tipo === "readonly" ? true : !isEditing,
            icon: field.icon,
        };

        switch (field.tipo) {
            case "moneda":
                return (
                    <div key={index} className="w-full">
                        <CurrencyInput
                            {...commonProps}
                            currency={field.currency || "USD"}
                        />
                    </div>
                );
            case "fecha":
                return (
                    <div key={index} className="w-full">
                        <DateInput
                            {...commonProps}
                            onChange={(newValue) => handleDateChange(field.fieldName, newValue)}
                        />
                    </div>
                );
            case "mes":
                return (
                    <div key={index} className="w-full">
                        <MonthSelect
                            {...commonProps}
                        />
                    </div>
                );
            case "año":
                return (
                    <div key={index} className="w-full">
                        <YearInput
                            {...commonProps}
                        />
                    </div>
                );
            case "readonly":
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            isReadOnly={true}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
            default:
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
        }
    };

    return (
        <div>
            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Identificación</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {identificationFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Referencias y Montos</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {referenceFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Información Legal</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {legalFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />
            
            {!readOnlyClaims && (
                <div className="flex justify-between">
                    <Button
                        variant="ghost"
                        color="primary"
                        onPress={toggleEditMode}
                        startContent={<Icon icon={isEditing ? "lucide:lock" : "lucide:unlock"} />}
                    >
                        {isEditing ? "Lock edition" : "Edit"}
                    </Button>

                    <Button color="primary" onPress={handleSave} isDisabled={!isEditing}>
                        Save
                    </Button>
                </div>
            )}
        </div>
    );
}

export default DetalleJudicialEditar; 